import { useEffect, useRef } from 'react';
import { useOfflineCheck } from 'hooks/useOfflineCheck';

const UpdateAlert = () => {
  const updateAvailable = useRef(false);
  const isReloadingForUpdate = useRef(false);
  const isOffline = useOfflineCheck();

  // Poll for version changes
  useEffect(() => {
    const checkUpdate = () => {
      fetch(`/version.txt?_=${Date.now()}`, {
        cache: 'no-store',
      })
        .then((res) => res.text())
        .then((version) => {
          if (
            !version.includes('<!') &&
            version.trim() !== process.env.REACT_APP_COMMIT_REF
          ) {
            updateAvailable.current = true;
          }
        })
        // eslint-disable-next-line no-console
        .catch((e) => console.log(e));
    };

    const checkUpdateIfOnline = () => {
      if (!isOffline) checkUpdate();
    };

    const onVisibilityChange = () => {
      if ('visible' === document.visibilityState) {
        checkUpdateIfOnline();
      }
    };

    if (!isOffline) checkUpdate();

    let interval;
    if (!isOffline) interval = setInterval(checkUpdate, 3000);

    window.addEventListener('focus', checkUpdateIfOnline);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (interval) clearInterval(interval);
      window.removeEventListener('focus', checkUpdateIfOnline);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isOffline]);

  // Intercept navigation and back/forward — on next route change, do a full page reload
  useEffect(() => {
    const originalPushState = window.history.pushState.bind(window.history);
    const originalReplaceState = window.history.replaceState.bind(
      window.history
    );

    const unregisterServiceWorkersAndReload = () => {
      const reloadCurrentPage = () => window.location.reload();

      // Unregister service workers so reload fetches fresh content
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker
          .getRegistrations()
          .then((registrations) =>
            Promise.all(
              registrations.map((registration) => registration.unregister())
            )
          )
          .then(reloadCurrentPage)
          .catch(reloadCurrentPage);
      } else {
        reloadCurrentPage();
      }
    };

    const reloadCurrentPageForUpdate = () => {
      if (!updateAvailable.current || isReloadingForUpdate.current) {
        return false;
      }

      isReloadingForUpdate.current = true;
      unregisterServiceWorkersAndReload();
      return true;
    };

    const reloadForUpdate = (state, title, url, navigationMethod) => {
      if (!updateAvailable.current || !url || isReloadingForUpdate.current) {
        return false;
      }

      isReloadingForUpdate.current = true;

      // Preserve the destination URL and history state before the reload so
      // routes that depend on location.state still work after the refresh.
      if ('replace' === navigationMethod) {
        originalReplaceState(state, title, url);
      } else {
        originalPushState(state, title, url);
      }

      unregisterServiceWorkersAndReload();
      return true;
    };

    const onPopState = () => {
      reloadCurrentPageForUpdate();
    };

    window.history.pushState = function (state, title, url) {
      if (reloadForUpdate(state, title, url, 'push')) return;
      return originalPushState(state, title, url);
    };

    window.history.replaceState = function (state, title, url) {
      if (reloadForUpdate(state, title, url, 'replace')) return;
      return originalReplaceState(state, title, url);
    };

    window.addEventListener('popstate', onPopState);

    return () => {
      window.removeEventListener('popstate', onPopState);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  return null;
};

export default UpdateAlert;
